import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Artifact, ArtifactType, ArtifactStatus, SAMPLE_ARTIFACTS } from './artifacts/types';
import { ArtifactCard } from './artifacts/ArtifactCard';
import { ArtifactFullScreen } from './artifacts/ArtifactFullScreen';
import { EmptyState } from './EmptyState';
type FilterType = 'all' | 'needs-action' | ArtifactType;
const FILTER_CHIPS: {
  id: FilterType;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'needs-action',
  label: 'Needs Action'
}, {
  id: 'email-draft',
  label: 'Email Drafts'
}, {
  id: 'document',
  label: 'Documents'
}, {
  id: 'screenshot',
  label: 'Screenshots'
}, {
  id: 'video',
  label: 'Videos'
}, {
  id: 'code-diff',
  label: 'Code'
}, {
  id: 'preview-url',
  label: 'Previews'
}];
export function ArtifactsView() {
  const [artifacts, setArtifacts] = useState<Artifact[]>(SAMPLE_ARTIFACTS);
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedArtifact, setExpandedArtifact] = useState<Artifact | null>(null);
  const filtered = useMemo(() => {
    let list = [...artifacts];
    if (filter === 'needs-action') {
      list = list.filter(a => a.status === 'pending' || a.status === 'refined');
    } else if (filter !== 'all') {
      list = list.filter(a => a.type === filter);
    }
    // Pending first, then by title
    list.sort((a, b) => {
      const aPending = a.status === 'pending' || a.status === 'refined' ? 0 : 1;
      const bPending = b.status === 'pending' || b.status === 'refined' ? 0 : 1;
      return aPending - bPending;
    });
    return list;
  }, [artifacts, filter]);
  const handleApprove = (id: string) => {
    setArtifacts(prev => prev.map(a => a.id === id ? {
      ...a,
      status: 'approved' as ArtifactStatus
    } : a));
  };
  const handleRefine = (id: string, _comment: string) => {
    setArtifacts(prev => prev.map(a => a.id === id ? {
      ...a,
      status: 'refining' as ArtifactStatus
    } : a));
    setTimeout(() => {
      setArtifacts(prev => prev.map(a => a.id === id ? {
        ...a,
        status: 'pending' as ArtifactStatus
      } : a));
    }, 2000);
  };
  const handleDiscard = (id: string) => {
    setArtifacts(prev => prev.map(a => a.id === id ? {
      ...a,
      status: 'discarded' as ArtifactStatus
    } : a));
  };
  return <div className="min-h-[calc(100vh-56px)] pt-16 pb-32 px-6" data-id="element-278">
      <div className="max-w-[900px] mx-auto w-full" data-id="element-279">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-6" data-id="element-280">Artifacts</h1>

        {/* Filter chips */}
        <div className="flex gap-2 mb-8 flex-wrap" data-id="element-281">
          {FILTER_CHIPS.map(chip => <button key={chip.id} onClick={() => setFilter(chip.id)} className={`px-3 py-1 rounded-full text-[12px] transition-colors ${filter === chip.id ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-white/[0.04] text-[#8a8680] hover:text-[#e8e4df]'}`} data-id="element-282">
              {chip.label}
            </button>)}
        </div>

        {filtered.length === 0 ? <EmptyState message="No artifacts match this filter." data-id="element-283" /> : <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4" initial={false} data-id="element-284">
            {filtered.map(artifact => <ArtifactCard key={artifact.id} artifact={artifact} onExpand={() => setExpandedArtifact(artifact)} onApprove={() => handleApprove(artifact.id)} onRefine={comment => handleRefine(artifact.id, comment)} onDiscard={() => handleDiscard(artifact.id)} data-id="element-285" />)}
          </motion.div>}
      </div>

      <ArtifactFullScreen artifact={expandedArtifact} onClose={() => setExpandedArtifact(null)} onApprove={() => expandedArtifact && handleApprove(expandedArtifact.id)} onRefine={comment => expandedArtifact && handleRefine(expandedArtifact.id, comment)} onDiscard={() => expandedArtifact && handleDiscard(expandedArtifact.id)} data-id="element-286" />
    </div>;
}